import Journey from "@/domains/-journey/models/Journey.ts";
import {WorkspacePersonMe} from "@/domains/-session/models/WorkspacePersonMe.ts";
import {useSessionStore} from "@/domains/-session/stores/useSessionStore.ts";
import {WorkspacePerson} from "@/domains/-workspace/models/WorkspacePerson.ts";
import {assertNever} from "@/functions/assertNever.ts";
import {reactive} from "vue";

export type Permission =
  | 'create_coachee'
  | 'update_coachee'
  | 'delete_coachee'
  | 'create_staff'
  | 'update_staff'
  | 'delete_staff'
  | 'access_promotions'
  | 'send_email_invitation'
  | 'select_meeting_coachs'

function canJourneyBeDeleted(journey: Journey): boolean {
  return journey.schema.deletion_guards.every(stepId => {
    const step = journey.getStepById(stepId);

    if (!step) return true;

    return !step.started_at;
  })
}

function canCoacheeBeDeleted(coachee: WorkspacePerson): boolean {
  return coachee.journeys.every(canJourneyBeDeleted)
}

export function usePermissionManager() {
  function check(cb: (me: WorkspacePersonMe) => boolean) {
    const session = useSessionStore();

    const me = session.wsp;
    if (!me) return false; // No current user
    return cb(me);
  }

  return reactive({
    get canIAccessPromotions() {
      return check(me => me.workspace.config.allow_promotion);
    },
    get canISelectMeetingCoachs() {
      return check(me => !!me.coach && me.workspace.allow_meeting_coach_selection)
    },
    get canIAssignExtern() {
      return check(me => !!me.coach && me.workspace.externs.length > 0);
    },
    get canISeeAssignedExtern() {
      return check(me => !!me.coach);
    },
    get canICreateCoachee(): boolean {
      return check(me => !!me.coach);
    },
    get canIUpdateCoachee(): boolean {
      return check(me => !!me.coach);
    },
    canIDeleteCoachee(coachees?: WorkspacePerson[]): boolean {
      return check(me => {
        return !!me.coach && (!coachees || coachees.every(canCoacheeBeDeleted))
      });
    },
    get canICreateJourney(): boolean {
      return check(me => !!me.coach);
    },
    canIDeleteJourney(journey?: Journey): boolean {
      return check(me => {
        return !!me.coach && (journey === undefined || canJourneyBeDeleted(journey));
      });
    },
    get canIAccessCredits(): boolean {
      return check(me => !!me.coach);
    },
    get canIAccessReports(): boolean {
      return check(me => !!me.coach || !!me.extern);
    },
    get canIUnlockAscent(): boolean {
      return check(me => !!me.coach);
    },
    canI(permission: Permission): boolean {
      return check(me => {
        switch (permission) {
          case 'create_coachee':
            return !!me.coach;
          case 'update_coachee':
            return !!me.coach;
          case 'delete_coachee':
            return !!me.coach;
          case 'create_staff':
            return !!me.admin;
          case 'update_staff':
            return !!me.admin;
          case 'delete_staff':
            return !!me.admin;
          case 'access_promotions':
            return me.workspace.config.allow_promotion;
          case 'send_email_invitation':
            return me.workspace.allow_automatic_email;
          case 'select_meeting_coachs':
            return me.workspace.allow_meeting_coach_selection;
          default:
            assertNever(permission);
        }
      })
    }
  });
}